import { ICellRendererParams } from "ag-grid-community";
import { useMemo } from "react";

import { TruncatedPermissionsBadges } from "../TruncatedPermissionsBadges";

import { TableColumn } from "@/components/ui/TableV2";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { Role } from "@/types/organization-roles";
import { Permissions } from "@/providers/RbacProvider/constants";

export const useRolesColumns = () => {
  const columns = useMemo((): TableColumn<Role>[] => {
    const baseColumns: TableColumn<Role>[] = [
      {
        id: 1,
        name: "name",
        field: "name",
        header: "Name",
        minWidth: 200,
        suppressHeaderMenuButton: true,
        cellRenderer: (params: ICellRendererParams<Role>) => {
          const role = params.data;
          if (!role) {
            return "";
          }

          const isWorkspaceAdmin = role.permissions.includes(Permissions.ADMIN);

          return (
            <div className="flex h-full w-full items-center">
              <OverflowDisplayTooltip content={role.name} shouldShow={Boolean(role.name)}>
                <span className="text-sm font-medium text-gray-900">{role.name}</span>
              </OverflowDisplayTooltip>
              {isWorkspaceAdmin && (
                <BasicTooltip content="Admin role">
                  <span className="ml-1 text-xs font-bold text-red-500">*</span>
                </BasicTooltip>
              )}
            </div>
          );
        },
      },
      {
        id: 2,
        name: "permissions",
        field: "permissions",
        header: "Permissions",
        headerName: "Permissions",
        sortable: false,
        suppressHeaderMenuButton: true,
        flex: 1,
        cellRenderer: (params: ICellRendererParams<Role>) => {
          const role = params.data;
          if (!role) {
            return "";
          }

          const permissions = role.permissions
            .filter((permission): permission is string => Boolean(permission))
            .sort((a, b) => a.localeCompare(b));

          return <TruncatedPermissionsBadges permissions={permissions} />;
        },
      },
    ];

    return baseColumns;
  }, []);

  return { columns };
};
